/**
 * SampleReportPreview — inline onboarding peek + prominent CTA.
 *
 * Design principles:
 *  - Reuse the existing Executive Growth Report™ UI for the full sample
 *    via a "View Full Sample Report" action that opens `/sample-report`
 *    in a new tab (so the consultant never loses their onboarding state).
 *  - Provide a small on-page snapshot so consultants see the payoff
 *    without leaving the flow.
 *  - Zero API calls. Zero new libraries. Responsive.
 */
import React from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import {
  ExternalLink,
  Eye,
  Sparkles,
  ShieldAlert,
  TrendingUp,
  BadgeCheck,
} from "lucide-react";
import { SAMPLE_LEAD } from "../lib/sample-report-data";

// ---- Small helpers ---------------------------------------------------------
const bandColor = (band) =>
  band === "Green"
    ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10"
    : band === "Amber"
    ? "text-amber-300 border-amber-500/40 bg-amber-500/10"
    : "text-rose-300 border-rose-500/40 bg-rose-500/10";

const severityForScore = (s) =>
  s < 65
    ? "text-rose-300 bg-rose-500/10 border-rose-500/40"
    : s < 80
    ? "text-amber-300 bg-amber-500/10 border-amber-500/40"
    : "text-emerald-300 bg-emerald-500/10 border-emerald-500/40";

// Inline citation pill (visual only — full clickable version lives on
// /sample-report through GroundingWidgets).
function CitationPill({ id }) {
  return (
    <span
      className="inline-flex items-center align-middle mx-0.5 h-5 px-1.5 rounded-md text-[10px] font-sans-ui font-semibold bg-accent/15 text-accent border border-accent/30"
      data-testid={`sample-citation-${id.toLowerCase()}`}
    >
      {id}
    </span>
  );
}

function GroundedSummary({ text }) {
  const parts = String(text).split(/(\[[ER]\d+\])/g);
  return (
    <p className="text-sm leading-relaxed text-muted-foreground">
      {parts.map((part, i) =>
        /^\[[ER]\d+\]$/.test(part) ? (
          <CitationPill key={i} id={part.replace(/[\[\]]/g, "")} />
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </p>
  );
}

// ---- Main ------------------------------------------------------------------
export default function SampleReportPreview() {
  const { results, report, assessment } = SAMPLE_LEAD;
  const categoryEntries = Object.entries(results.category_scores);

  return (
    <Card
      className="mt-8 card-elevated p-5 sm:p-6"
      data-testid="sample-report-preview"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-accent font-sans-ui">
            <Eye className="w-3.5 h-3.5" />
            Sample · Executive Growth Report
          </div>
          <div className="mt-1.5 font-display text-xl text-foreground">
            Al Nahda Manufacturing
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {assessment.country} · {assessment.industry} ·{" "}
            {assessment.function} · CFO
          </div>
        </div>
        <div
          className={`rounded-xl border px-4 py-2.5 text-right ${bandColor(
            results.risk_level
          )}`}
          data-testid="sample-score-card"
        >
          <div className="text-[10px] uppercase tracking-widest opacity-80">
            Business Pulse
          </div>
          <div className="font-display text-2xl leading-tight mt-0.5">
            {results.overall_score}
          </div>
          <div className="text-[11px] mt-0.5">
            {results.risk_level} band
          </div>
        </div>
      </div>

      {/* Category scores */}
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
        {categoryEntries.map(([name, value]) => (
          <div key={name} className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between text-xs">
                <span className="truncate text-muted-foreground">{name}</span>
                <span className="text-foreground font-sans-ui font-semibold ml-2">
                  {value}
                </span>
              </div>
              <div className="mt-1 h-1 rounded-full bg-border/60 overflow-hidden">
                <div
                  className="h-full bg-accent"
                  style={{ width: `${value}%` }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Grounded executive summary snippet */}
      <div className="mt-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5 font-sans-ui">
          <Sparkles className="w-3 h-3 text-accent" /> Executive summary
          <span className="text-accent/70 normal-case tracking-normal">
            (grounded)
          </span>
        </div>
        <GroundedSummary text={report.executive_summary} />
      </div>

      {/* Top opportunities */}
      <div className="mt-5">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 font-sans-ui">
          Top opportunities
        </div>
        <ul className="space-y-1.5" data-testid="sample-top-gaps">
          {results.top_gaps.slice(0, 3).map((g) => (
            <li
              key={g.area}
              className="flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-background/40 px-3 py-2"
            >
              <div className="flex items-center gap-2 min-w-0">
                <TrendingUp className="w-3.5 h-3.5 text-accent shrink-0" />
                <span className="text-sm font-sans-ui text-foreground truncate">
                  {g.area}
                </span>
              </div>
              <span
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-sans-ui shrink-0 ${severityForScore(
                  g.score
                )}`}
              >
                <ShieldAlert className="w-3 h-3" />
                {g.score}/100
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Prominent CTA */}
      <div className="mt-6 pt-5 border-t border-border/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="text-xs text-muted-foreground">
          <BadgeCheck className="inline-block w-3.5 h-3.5 text-accent mr-1 -mt-0.5" />
          Includes grounded citations, regulatory obligations & a 30/90/180-day roadmap.
        </div>
        <a
          href="/sample-report"
          target="_blank"
          rel="noreferrer"
          data-testid="sample-report-view-full"
        >
          <Button size="lg" className="px-6">
            View Full Sample Report
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </a>
      </div>
    </Card>
  );
}
